import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { useNavigate } from "@tanstack/react-router";

import { useAppDispatch } from "@/store/hooks";
import { initializeRequests } from "@/store/slices/requestsSlice";
import { initializeTables } from "@/store/slices/advancedTableSlice";
import { initializeWallpaperStore } from "@/store/slices/wallpaperSlice";
import { setRequestAccessToken } from "@/lib/request";
import { userQueryOptions } from "@/hooks/queries/userQuery";
import { UserContext } from "@/lib/context";
import { emptyStorageUser } from "@/lib/storage";
import { Authentication } from "../Authentication";
import { UserModal } from "../User/Modal";
import { SubscriptionsModal } from "../User/SubscriptionsModal";
import { LOCALSTORAGE_KEY } from "@/lib/constants";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/query";
import { toast } from "sonner";
import { ScreenSpinner } from "../ui/spinner";
import { isBefore } from "date-fns";
import { identifyUser, resetUser } from "@/lib/analytics";
import { maybeTrackDay2Return } from "@/lib/onboarding";

export const UserProvider = ({
  resetPasswordToken,
  changePasswordToken,
  children,
}: {
  resetPasswordToken?: string;
  changePasswordToken?: string;
  children: React.ReactNode;
}) => {
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticationModalOpen, setAuthenticationModalOpen] =
    useState<boolean>(!!resetPasswordToken);
  const [isUserModalOpen, setUserModalOpen] = useState<boolean>(false);
  const [isCreateProfileModalOpen, setCreateProfileModalOpen] = useState(false);
  const [showSubscriptionsModal, setShowSubscriptionsModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    ...userQueryOptions(accessToken),
    enabled: !!accessToken,
    retry(failureCount, error) {
      if (
        isAxiosError(error) &&
        (error.response?.status === 401 || error.response?.status === 403)
      ) {
        logout();
        return false;
      }

      if (failureCount >= 2) {
        toast.error(
          "Failed to load user profile after multiple attempts, please check your internet connection and try again.",
          {
            action: {
              label: "Try again",
              onClick: () => {
                refetch();
              },
            },
          },
        );
        return false;
      }

      return true;
    },
  });

  const unpaidSubscriptions = useMemo(
    () =>
      user?.ownedTeams?.filter(
        team =>
          team.subscription &&
          team.subscription.length > 0 &&
          team.subscription.some(
            subscription =>
              isBefore(subscription.endDate || new Date(0), new Date()) ||
              (subscription.teamSize || 0) < team.members.length,
          ),
      ) || [],
    [user],
  );

  const handleUpdateAccessToken = async (accessToken: string | null) => {
    localStorage.setItem("unideck.auth.accessToken", accessToken as string);
    setAccessToken(accessToken);
    setRequestAccessToken(accessToken);

    setTimeout(() => {
      const key = `${LOCALSTORAGE_KEY}-join-team-token`;
      if (accessToken && localStorage.getItem(key)) {
        const joinTeamToken = localStorage.getItem(key);
        if (joinTeamToken) {
          localStorage.removeItem(key);
          navigate({
            to: "/team/join",
            search: { token: joinTeamToken },
          });
        }
      }
    }, 1000);
  };

  const checkForToken = async () => {
    const accessToken = localStorage.getItem("unideck.auth.accessToken");

    if (!accessToken) {
      localStorage.removeItem("unideck.auth.accessToken");
      setLoading(false);
      return null;
    }

    setAccessToken(accessToken as string);
    setRequestAccessToken(accessToken as string);
    if (changePasswordToken) {
      setUserModalOpen(true);
    }
    setLoading(false);
  };

  const logout = async () => {
    await emptyStorageUser();
    setAccessToken(null);
    setRequestAccessToken(null);
    setUserModalOpen(false);
    setAuthenticationModalOpen(true);
    resetUser();
    queryClient.clear();
  };

  useEffect(() => {
    if (user?.new_user) {
      setCreateProfileModalOpen(true);
    }
  }, [user]);

  useEffect(() => {
    if (user && accessToken) {
      maybeTrackDay2Return();
    }
  }, [user, accessToken]);

  useEffect(() => {
    if (!user || !accessToken) {
      return;
    }

    identifyUser({
      distinctId: user.email,
      properties: {
        email: user.email,
        displayName: user.displayName,
        hasSubscription: Boolean(user.subscription),
        ownedTeamsCount: user.ownedTeams.length,
        joinedTeamsCount: user.joinedTeams.length,
        pendingTeamsCount: user.pendingTeams.length,
      },
    });
  }, [accessToken, user]);

  useEffect(() => {
    checkForToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(initializeWallpaperStore());
    dispatch(initializeRequests());
    dispatch(initializeTables());
  }, [accessToken, dispatch]);

  return (
    <UserContext.Provider
      value={{
        user: user || null,
        syncUser: refetch,
        accessToken,
        setAccessToken: handleUpdateAccessToken,
        isAuthenticationModalOpen,
        setAuthenticationModalOpen,
        isUserModalOpen,
        setUserModalOpen,
        isCreateProfileModalOpen,
        setCreateProfileModalOpen,
        isLoading,
        isError,
        error,
        logout,
        showSubscriptionsModal,
        setShowSubscriptionsModal,
        unpaidSubscriptions,
      }}
    >
      {loading ? (
        <div className="bg-background flex min-h-screen w-screen items-center justify-center">
          <div className="flex flex-col gap-4">
            <ScreenSpinner />
            <span className="text-muted-foreground animate-pulse">
              Fetching account information
            </span>
          </div>
        </div>
      ) : (
        children
      )}
      <Authentication resetPasswordToken={resetPasswordToken} />
      <UserModal
        open={isUserModalOpen}
        onOpenChange={setUserModalOpen}
        changePasswordToken={changePasswordToken}
      />
      <SubscriptionsModal
        open={showSubscriptionsModal}
        onOpenChange={setShowSubscriptionsModal}
      />
    </UserContext.Provider>
  );
};
